import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import Avatar, { MENTORS } from "../components/avatars/AvatarLibrary";
import { Button } from "../components/ui/button";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";

export default function MentorSelectPage() {
  const navigate = useNavigate();
  const { onboarding, checkAuth } = useAuth();
  const [selected, setSelected] = useState(onboarding?.mentor_id || null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Choose your mentor — Ascend Higher";
    track("mentor_select_view");
  }, []);

  // 4 human mentors. Photographic portraits, premium feel.
  const mentors = MENTORS.filter((m) => m.category === "human");

  const choose = async () => {
    if (!selected) {
      toast.error("Pick a mentor to continue.");
      return;
    }
    setSaving(true);
    try {
      const m = MENTORS.find((x) => x.id === selected);
      await axios.post(`${API}/onboarding`, {
        mentor_id: selected,
        coach_gender: m?.voice?.gender || "female",
        coach_accent: m?.voice?.accent || "american",
      });
      track("mentor_selected", { mentor_id: selected });
      if (checkAuth) await checkAuth();
      // First-time → start the conversational onboarding. Returning users → dashboard.
      const next = onboarding?.completed ? "/dashboard" : "/welcome";
      navigate(next);
    } catch (e) {
      toast.error("Couldn't save your mentor. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f3ea]" data-testid="mentor-select-page">
      <header className="px-6 pt-7 max-w-5xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-1">
          <span className="font-display text-[20px] font-semibold text-[#0f1724]">ascend</span>
          <span className="font-display text-[20px] font-semibold text-[#486a8f]">higher</span>
        </div>
        <button
          data-testid="mentor-skip-btn"
          onClick={() => navigate("/welcome")}
          className="font-body text-[13px] text-[#6b7280] hover:text-[#486a8f]"
        >
          Skip for now
        </button>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 md:py-14">
        <div className="text-center mb-8">
          <p className="font-mono-eyebrow text-[#486a8f] mb-3">PICK YOUR MENTOR</p>
          <h1 className="font-display text-[40px] md:text-[56px] leading-[1.05] text-[#0f1724] tracking-tight">
            Who do you want{" "}
            <span className="italic font-light text-[#486a8f]">in your corner?</span>
          </h1>
          <p className="font-body text-[15px] text-[#4b5563] mt-5 max-w-xl mx-auto">
            Your mentor's voice and presence will guide every conversation.
            You can change this any time.
          </p>
        </div>

        {/* Grid — 4 photographic mentors */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 max-w-5xl mx-auto">
          {mentors.map((m) => {
            const isSelected = selected === m.id;
            return (
              <button
                key={m.id}
                data-testid={`mentor-card-${m.id}`}
                onClick={() => {
                  setSelected(m.id);
                  track("mentor_card_click", { mentor_id: m.id });
                }}
                className={`relative rounded-3xl p-5 text-left transition-all border-2 ${
                  isSelected
                    ? "border-[#486a8f] bg-white shadow-[0_20px_50px_-20px_rgba(72,106,143,0.3)] scale-[1.02]"
                    : "border-[#eadfc9] bg-white hover:border-[#c8b88a]"
                }`}
              >
                <div className="flex items-center justify-center mb-4">
                  <Avatar id={m.id} size={140} state={isSelected ? "speaking" : "idle"} />
                </div>
                <p className="font-display text-[20px] text-[#0f1724] tracking-tight">
                  {m.label}
                </p>
                <p className="font-body text-[12px] text-[#486a8f] mt-1 font-medium">
                  {m.subtitle}
                </p>
                {m.bio && (
                  <p className="font-body text-[12px] text-[#6b7280] mt-2 leading-snug">
                    {m.bio}
                  </p>
                )}
                {isSelected && (
                  <div className="absolute top-4 right-4 w-7 h-7 rounded-full bg-[#486a8f] text-white flex items-center justify-center">
                    <Check size={14} />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-10 max-w-md mx-auto">
          <Button
            data-testid="mentor-continue-btn"
            onClick={choose}
            disabled={!selected || saving}
            className="w-full h-13 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white font-medium text-[15px] disabled:opacity-50"
          >
            {saving ? "Saving…" : "Continue with this mentor"}
            <ArrowRight size={15} className="ml-2" />
          </Button>
          <p className="font-body text-[12px] text-[#6b7280] text-center mt-3 inline-flex items-center justify-center gap-2 w-full">
            <Sparkles size={11} className="text-[#486a8f]" />
            You can switch your mentor any time from Settings.
          </p>
        </div>
      </main>
    </div>
  );
}
